import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CurrencyPolicyRecord, FinanceApiService } from './finance-api.service';

type CurrencyPolicyTab = 'general' | 'history';

@Component({
  standalone: true,
  selector: 'ain-finance-control-center-currency-policy-detail',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule, CurrencySelectorComponent],
  template: `
    <ain-page [title]="isCreateMode() ? t('finance.multiCurrency.policies.createTitle') : t('finance.multiCurrency.policies.detailTitle')" [description]="t('finance.multiCurrency.policies.detailDescription')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ t('finance.multiCurrency.policies.headerTitle') }}</h3>
            <p>{{ form.functionalCurrencyCode || '-' }} / {{ form.reportingCurrencyCode || '-' }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/financial/control-center/currency-policies']" [queryParams]="listQueryParams()" class="back-link">{{ t('catalog.action.backToList') }}</a>
            <button pButton type="button" severity="secondary" [label]="t('finance.multiCurrency.actions.openCurrencyMaster')" (click)="openCurrencyMaster()"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </div>
        </section>

        <section class="tabs">
          <button type="button" [class.active]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('catalog.tabs.general') }}</button>
          <button type="button" [class.active]="activeTab() === 'history'" (click)="activeTab.set('history')">{{ t('finance.multiCurrency.tabs.history') }}</button>
        </section>

        @if (activeTab() === 'general') {
          <section class="form-grid">
            <label>
              <span>{{ t('finance.multiCurrency.policies.functionalCurrency') }}</span>
              <ain-currency-selector [(ngModel)]="form.functionalCurrencyCode" [showSearch]="true"></ain-currency-selector>
            </label>
            <label>
              <span>{{ t('finance.multiCurrency.policies.reportingCurrency') }}</span>
              <ain-currency-selector [(ngModel)]="form.reportingCurrencyCode" [showSearch]="true"></ain-currency-selector>
            </label>
            <div class="radio-wrapper">
              <span>{{ t('finance.multiCurrency.policies.requireApproval') }}</span>
              <div class="radio-group">
                <label class="radio-item">
                  <input type="radio" name="policy-require-approval" [checked]="form.requireFxRateApproval" (change)="form.requireFxRateApproval = true" />
                  <span>{{ t('common.yes') }}</span>
                </label>
                <label class="radio-item">
                  <input type="radio" name="policy-require-approval" [checked]="!form.requireFxRateApproval" (change)="form.requireFxRateApproval = false" />
                  <span>{{ t('common.no') }}</span>
                </label>
              </div>
            </div>
            <div class="radio-wrapper">
              <span>{{ t('finance.multiCurrency.policies.allowCrossPosting') }}</span>
              <div class="radio-group">
                <label class="radio-item">
                  <input type="radio" name="policy-allow-cross" [checked]="form.allowCrossCurrencyPosting" (change)="form.allowCrossCurrencyPosting = true" />
                  <span>{{ t('common.yes') }}</span>
                </label>
                <label class="radio-item">
                  <input type="radio" name="policy-allow-cross" [checked]="!form.allowCrossCurrencyPosting" (change)="form.allowCrossCurrencyPosting = false" />
                  <span>{{ t('common.no') }}</span>
                </label>
              </div>
            </div>
          </section>
        }

        @if (activeTab() === 'history') {
          <section class="history">
            <article class="history-item">
              <h4>{{ t('finance.multiCurrency.policies.functionalCurrency') }}</h4>
              <p>{{ form.functionalCurrencyCode || '-' }}</p>
            </article>
            <article class="history-item">
              <h4>{{ t('finance.multiCurrency.policies.reportingCurrency') }}</h4>
              <p>{{ form.reportingCurrencyCode || '-' }}</p>
            </article>
            <article class="history-item">
              <h4>{{ t('finance.multiCurrency.policies.requireApproval') }}</h4>
              <p>{{ form.requireFxRateApproval ? t('common.yes') : t('common.no') }}</p>
            </article>
            <article class="history-item">
              <h4>{{ t('finance.multiCurrency.policies.allowCrossPosting') }}</h4>
              <p>{{ form.allowCrossCurrencyPosting ? t('common.yes') : t('common.no') }}</p>
            </article>
          </section>
        }

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
    .header-card h3 { margin: 0; }
    .header-card p { margin: 6px 0 0; color: #64748b; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .tabs { display: flex; gap: 8px; flex-wrap: wrap; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-top: 10px; }
    .tabs button { border: 1px solid #cbd5e1; border-radius: 999px; background: white; padding: 6px 12px; cursor: pointer; }
    .tabs button.active { border-color: #1d4ed8; color: #1d4ed8; font-weight: 700; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; margin-top: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid span { color: #475569; font-size: 12px; font-weight: 600; }
    .radio-wrapper { display: grid; gap: 6px; }
    .radio-group { display: flex; align-items: center; flex-wrap: wrap; gap: 14px; min-height: 2.5rem; }
    .radio-item { display: inline-flex; align-items: center; gap: 8px; }
    .history { display: grid; gap: 8px; margin-top: 10px; }
    .history-item { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; }
    .history-item h4 { margin: 0 0 6px; font-size: 14px; }
    .history-item p { margin: 0; color: #475569; font-size: 12px; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class FinanceControlCenterCurrencyPolicyDetailComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly activeTab = signal<CurrencyPolicyTab>('general');
  readonly isCreateMode = signal(false);
  readonly policy = signal<CurrencyPolicyRecord | null>(null);

  readonly form = {
    functionalCurrencyCode: '',
    reportingCurrencyCode: '',
    requireFxRateApproval: true,
    allowCrossCurrencyPosting: false
  };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id === 'new') {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    this.loadPolicy();
  }

  save(): void {
    this.error.set('');
    if (!this.form.functionalCurrencyCode.trim() || !this.form.reportingCurrencyCode.trim()) {
      this.error.set(this.t('finance.multiCurrency.errors.requiredFields'));
      return;
    }

    this.api.upsertCurrencyPolicy({
      functionalCurrencyCode: this.form.functionalCurrencyCode.trim(),
      reportingCurrencyCode: this.form.reportingCurrencyCode.trim(),
      requireFxRateApproval: this.form.requireFxRateApproval,
      allowCrossCurrencyPosting: this.form.allowCrossCurrencyPosting
    }).subscribe({
      next: saved => {
        this.policy.set(saved);
        this.isCreateMode.set(false);
        this.syncForm(saved);
        void this.router.navigate(['/financial/control-center/currency-policies', saved.id], { queryParams: this.listQueryParams(), replaceUrl: true });
      },
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  openCurrencyMaster(): void {
    void this.router.navigate(['/financial/control-center/currencies']);
  }

  private loadPolicy(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.getCurrencyPolicy().subscribe({
      next: row => {
        this.policy.set(row);
        this.syncForm(row);
        this.loading.set(false);
      },
      error: (err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          this.isCreateMode.set(true);
          this.loading.set(false);
          return;
        }

        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  private syncForm(row: CurrencyPolicyRecord): void {
    this.form.functionalCurrencyCode = row.functionalCurrencyCode;
    this.form.reportingCurrencyCode = row.reportingCurrencyCode;
    this.form.requireFxRateApproval = row.requireFxRateApproval;
    this.form.allowCrossCurrencyPosting = row.allowCrossCurrencyPosting;
  }

  listQueryParams(): Record<string, string> {
    return {
      functionalCurrencyCode: this.route.snapshot.queryParamMap.get('functionalCurrencyCode') ?? '',
      reportingCurrencyCode: this.route.snapshot.queryParamMap.get('reportingCurrencyCode') ?? '',
      requireApproval: this.route.snapshot.queryParamMap.get('requireApproval') ?? '',
      showFilters: this.route.snapshot.queryParamMap.get('showFilters') ?? '1'
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
